import type { LoanListItem } from '@app/shared';
import { cellText, type ColKind, type Column } from './columns';

// ---------------------------------------------------------------------------
// Filtering + sorting, entirely client-side.
//
// The list endpoint returns the full matching set in one response (no server
// paging), so every filter here runs over data already in the browser. That is
// what lets derived fields like `lineStatus` filter identically to stored ones,
// and what makes the value pickers able to show live counts.
// ---------------------------------------------------------------------------

export type Operator =
  | 'equals' | 'notEquals'          // enum / bool — multi-select, any-of
  | 'contains' | 'startsWith'       // text
  | 'before' | 'after' | 'between'  // date
  | 'gt' | 'lt' | 'eq'              // money
  | 'isEmpty' | 'notEmpty';         // any

export type Filter = {
  /** Stable key so React can track chips across edits. */
  key: string;
  columnId: string;
  op: Operator;
  /** Selected values for multi-select ops; [from, to] for `between`; [v] otherwise. */
  values: string[];
  /**
   * Display label per selected value, captured at selection time.
   *
   * Necessary because a chip's own label must not depend on the rows currently
   * visible: sibling filters can narrow the set to nothing, and re-deriving the
   * label then would find no row carrying that value and fall back to showing
   * the raw key — an OID, for the assignment columns.
   */
  labels?: Record<string, string>;
};

export const OPERATOR_LABELS: Record<Operator, string> = {
  equals: 'equals',
  notEquals: 'does not equal',
  contains: 'contains',
  startsWith: 'starts with',
  before: 'before',
  after: 'after',
  between: 'between',
  gt: 'greater than',
  lt: 'less than',
  eq: 'equals',
  isEmpty: 'is empty',
  notEmpty: 'is not empty',
};

const OPS_BY_KIND: Record<ColKind, Operator[]> = {
  text:  ['contains', 'equals', 'startsWith', 'isEmpty', 'notEmpty'],
  enum:  ['equals', 'notEquals', 'isEmpty', 'notEmpty'],
  bool:  ['equals'],
  date:  ['after', 'before', 'between', 'isEmpty', 'notEmpty'],
  money: ['gt', 'lt', 'between', 'eq', 'isEmpty', 'notEmpty'],
};

export const operatorsFor = (c: Column): Operator[] => OPS_BY_KIND[c.kind];

/**
 * Whether the value editor is the multi-select checklist rather than a free input.
 * Depends on the column too: "equals" on an enum means pick from the known values,
 * but on a free-text column like Borrower it means type an exact string — a
 * checklist of several thousand distinct borrower names would be useless.
 */
export const isMultiSelect = (op: Operator, kind: ColKind) =>
  (kind === 'enum' || kind === 'bool') && (op === 'equals' || op === 'notEquals');
/** Ops that take no value at all. */
export const takesNoValue = (op: Operator) => op === 'isEmpty' || op === 'notEmpty';

export function defaultOperator(c: Column): Operator {
  return OPS_BY_KIND[c.kind][0];
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

const norm = (s: string) => s.trim().toLowerCase();

function matches(c: Column, f: Filter, row: LoanListItem): boolean {
  const raw = c.value(row);
  const empty = raw == null || raw === '';

  if (f.op === 'isEmpty') return empty;
  if (f.op === 'notEmpty') return !empty;

  // A filter with nothing selected/entered is inert rather than all-excluding —
  // matching the "equals all" resting state of the chips. Note the empty string
  // is a *selectable value* for multi-select (it's the Unassigned / (empty)
  // bucket), so only the scalar operators treat it as "nothing entered".
  const vals = isMultiSelect(f.op, c.kind) ? f.values : f.values.filter(v => v !== '');
  if (!vals.length) return true;

  if (isMultiSelect(f.op, c.kind)) {
    // Bools compare on their display text ('Yes'/'No'); everything else on
    // the raw value, so the picker and the data agree exactly.
    //
    // Case-insensitively, because the assignment columns match on OIDs: SQL Server
    // returns GUIDs uppercase while sign-in claims are lowercase, and an exact
    // compare here silently emptied the "assigned to me" chip once already (fixed
    // at the API boundary in 87a7a66 — this is the belt to that braces). Enum
    // values elsewhere come from one shared list, so folding case costs nothing.
    const cmp = c.kind === 'bool' ? cellText(c, row) : (empty ? '' : String(raw));
    const hit = vals.some(v => v === cmp || norm(v) === norm(cmp));
    return f.op === 'equals' ? hit : !hit;
  }

  if (c.kind === 'money') {
    if (empty) return false;
    const n = Number(raw);
    const a = Number(vals[0]);
    if (f.op === 'between') {
      const b = Number(vals[1]);
      if (Number.isNaN(a) || Number.isNaN(b)) return true;
      return n >= Math.min(a, b) && n <= Math.max(a, b);
    }
    if (Number.isNaN(a)) return true;
    if (f.op === 'gt') return n > a;
    if (f.op === 'lt') return n < a;
    return n === a;
  }

  if (c.kind === 'date') {
    if (empty) return false;
    // Dates are ISO (YYYY-MM-DD) end to end, so lexical compare is chronological.
    const d = String(raw).slice(0, 10);
    if (f.op === 'between') {
      if (!vals[0] || !vals[1]) return true;
      const [lo, hi] = vals[0] <= vals[1] ? [vals[0], vals[1]] : [vals[1], vals[0]];
      return d >= lo && d <= hi;
    }
    if (f.op === 'after') return d >= vals[0];
    return d <= vals[0];
  }

  // text
  const hay = norm(cellText(c, row));
  const needle = norm(vals[0]);
  if (f.op === 'equals') return hay === needle;
  if (f.op === 'startsWith') return hay.startsWith(needle);
  return hay.includes(needle);
}

/** Rows passing every filter except the one with `exceptKey` (used for live value counts). */
export function applyFilters(
  rows: LoanListItem[],
  filters: Filter[],
  columnOf: (id: string) => Column | undefined,
  exceptKey?: string,
): LoanListItem[] {
  const active = filters.filter(f => f.key !== exceptKey);
  if (!active.length) return rows;
  return rows.filter(row => active.every(f => {
    const c = columnOf(f.columnId);
    return !c || matches(c, f, row);
  }));
}

/** The "Filter for any field" box: case-insensitive contains across the visible columns. */
export function applyQuickSearch(rows: LoanListItem[], q: string, columns: Column[]): LoanListItem[] {
  const needle = norm(q);
  if (!needle) return rows;
  return rows.filter(r => columns.some(c => norm(cellText(c, r)).includes(needle)));
}

// ---------------------------------------------------------------------------
// Value pickers
// ---------------------------------------------------------------------------

export type ValueOption = { value: string; label: string; count: number };

/**
 * Distinct values for a column with a count of matching rows, ordered by the
 * column's canonical option list where it has one and by frequency otherwise.
 * `rows` should already have the *other* filters applied, so counts reflect
 * what selecting the value would actually yield.
 */
/**
 * Add the chip's own selected values to an option list that does not contain them.
 *
 * `valueOptions` builds its list from the rows in the pool, so a value nothing currently
 * matches is simply absent. For most values that is right — there is no reason to offer
 * "Litigation" when no loan is in litigation. It is wrong for values the chip has already
 * selected, and it produced a filter that could not be edited:
 *
 * A Special Assets officer's default chip is "mine or Unassigned". A loan only leaves
 * Prepurchase when someone submits a Purchase request, and that request assigns the SA
 * officer — so among the loans the default view shows, none are SA-unassigned. The
 * Unassigned half of their own filter had no row behind it, so it vanished from the list:
 * invisible, impossible to untick, and silently discarded by "Select all", which rebuilds
 * the selection from the visible options only. Loan analysts never saw it, because their
 * assignment is set later and plenty of loans still have no analyst.
 *
 * The fix is to show such a value with a count of zero — it is selected, it just matches
 * nothing here. Labels come from the chip, which captured them when they were selected;
 * `defaultFiltersFor` supplies them up front for exactly this reason.
 *
 * `selected` should be the chip's values as the editor opened, not its live selection.
 * Otherwise unticking one of these makes it disappear mid-edit, which is its own surprise.
 */
export function withSelectedOptions(
  options: ValueOption[],
  selected: readonly string[],
  c: Column,
  labels?: Record<string, string>,
): ValueOption[] {
  const present = new Set(options.map(o => o.value));
  const missing = selected.filter(v => !present.has(v)).map(v => ({
    value: v,
    label: labels?.[v] ?? (v === '' ? (c.emptyLabel ?? '(empty)') : v),
    count: 0,
  }));
  // First, because they are selected: a checked box the reader has to scroll for is
  // barely better than one that is not there.
  return missing.length > 0 ? [...missing, ...options] : options;
}

export function valueOptions(c: Column, rows: LoanListItem[]): ValueOption[] {
  const counts = new Map<string, { label: string; count: number }>();
  for (const r of rows) {
    const raw = c.value(r);
    const isEmpty = raw == null || raw === '';
    const value = c.kind === 'bool' ? cellText(c, r) : (isEmpty ? '' : String(raw));
    const label = isEmpty && c.kind !== 'bool'
      ? (c.emptyLabel ?? '(empty)')
      : (cellText(c, r) || String(value));
    const prev = counts.get(value);
    if (prev) prev.count++;
    else counts.set(value, { label, count: 1 });
  }

  const out = [...counts].map(([value, { label, count }]) => ({ value, label, count }));

  // A column that names its empty state (Unassigned) means that state is a
  // first-class choice, not a leftover — so it pins to the top of the list,
  // directly under "All", rather than sorting by frequency with everyone else.
  const pinned = (o: ValueOption) => c.emptyLabel && o.value === '' ? 0 : 1;

  if (c.options) {
    const order = new Map(c.options.map((o, i) => [o, i]));
    return out.sort((a, b) =>
      pinned(a) - pinned(b)
      || (order.get(a.value) ?? 998) - (order.get(b.value) ?? 998)
      || a.label.localeCompare(b.label));
  }
  return out.sort((a, b) =>
    pinned(a) - pinned(b)
    || b.count - a.count
    || a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

export type SortDir = 'asc' | 'desc';

/** Which column a table is ordered by, and which way. */
export type SortSpec = { id: string; dir: SortDir };

/**
 * Compare by column kind. Empty values always sort last regardless of
 * direction — an undated loan is not "earliest", it's unscheduled.
 */
export function compareBy(c: Column, dir: SortDir) {
  const mul = dir === 'asc' ? 1 : -1;
  const order = c.options ? new Map(c.options.map((o, i) => [o, i])) : null;
  // Assignment columns filter on OID but must sort by the person's name.
  const key = c.sortValue ?? c.value;

  return (a: LoanListItem, b: LoanListItem): number => {
    const av = key(a);
    const bv = key(b);
    const aEmpty = av == null || av === '';
    const bEmpty = bv == null || bv === '';
    if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;

    if (order) {
      // Enums sort in lifecycle order, not alphabetically — Prepurchase →
      // Liquidation → Charge Off → Closed reads as progress.
      const ai = order.get(String(av)) ?? Number.MAX_SAFE_INTEGER;
      const bi = order.get(String(bv)) ?? Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return (ai - bi) * mul;
      return String(av).localeCompare(String(bv)) * mul;
    }
    if (c.kind === 'money') return (Number(av) - Number(bv)) * mul;
    if (c.kind === 'bool') return ((av ? 1 : 0) - (bv ? 1 : 0)) * mul;
    // Dates are ISO, so string compare is chronological.
    return String(av).localeCompare(String(bv), undefined, { numeric: c.kind === 'text' }) * mul;
  };
}

// ---------------------------------------------------------------------------
// Chip summaries
// ---------------------------------------------------------------------------

/**
 * A chip's three parts, rendered in different weights/colours: field name,
 * operator, then the value. e.g. "Phase" / "equals" / "Liquidation +2".
 * `value` is empty for the valueless operators.
 */
export type ChipParts = { field: string; op: string; value: string };

export function summarize(c: Column, f: Filter, options: ValueOption[]): ChipParts {
  const parts = (value: string): ChipParts => ({ field: c.header, op: OPERATOR_LABELS[f.op], value });

  if (takesNoValue(f.op)) return parts('');

  // An empty selection reads as "all" — the filter is present but not yet
  // narrowing anything, which is also how `matches` treats it.
  const vals = f.values.filter(v => v !== '' || isMultiSelect(f.op, c.kind));
  if (!vals.length) return parts('all');

  if (isMultiSelect(f.op, c.kind)) {
    // Captured label first, then the live options, then the raw value.
    const labelOf = (v: string) =>
      f.labels?.[v]
      || options.find(o => o.value === v)?.label
      || (v === '' ? (c.emptyLabel ?? '(empty)') : v);
    const first = labelOf(vals[0]);
    return parts(vals.length === 1 ? first : `${first} +${vals.length - 1}`);
  }
  if (f.op === 'between') return parts(`${vals[0]} – ${vals[1] ?? ''}`);
  return parts(vals[0]);
}

let seq = 0;
export const newFilterKey = () => `f${++seq}`;
